import {
  ArrowRight,
  ChevronRight,
  File,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FolderClosed,
  FolderPlus,
  Info,
  Link2,
  Loader2,
  MoreHorizontal,
  RotateCcw,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { StatusBadge } from '@/components/documents/DocumentBadges'
import { DocumentRowPhoneMeta } from '@/components/documents/DocumentRow'
import {
  HEADER_CHECKBOX_HIT_AREA,
  ROW_CHECKBOX_HIT_AREA,
} from '@/components/documents/document-grid'
import { DocumentsStatsSheet } from '@/components/lake/DocumentsStatsSheet'
import { LakeDetailSheet } from '@/components/lake/LakeDetailSheet'
import type { FolderSelection } from '@/components/lake/LakeFolderPickerSheet'
import { LakeFolderPickerSheet } from '@/components/lake/LakeFolderPickerSheet'
import { LakeSearchCombobox } from '@/components/lake/LakeSearchCombobox'
import { LakeUpload } from '@/components/lake/LakeUpload'
import { PageShell } from '@/components/layout/PageShell'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { FilterChip } from '@/components/ui/filter-chip'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { caseDocumentImportApi } from '@/services/api/case-document-import'
import type { Case } from '@/services/api/cases'
import { casesApi } from '@/services/api/cases'
import type {
  DataLabel,
  DocumentStatusFilter,
  FileNode,
  FolderNode,
  LakeStats,
  StatusCounts,
} from '@/services/api/lake'
import { lakeApi } from '@/services/api/lake'
import { isAudioFileType } from '@/utils/audio-playback'
import { cn } from '@/utils/cn'

// ── Helpers ─────────────────────────────────────────────────────────────────

type SortField = 'name' | 'type' | 'size' | 'status' | 'modified'
type SortDir = 'asc' | 'desc'

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'indexed', label: 'Indexed' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
] as const

/** The phone keeps checkbox, name and row menu; Type, Size, Status and Modified join from md up. */
const TABLE_GRID =
  'grid grid-cols-[28px_minmax(0,1fr)_32px] md:grid-cols-[28px_minmax(0,1fr)_60px_70px_90px_80px_28px] gap-2'
const TABLE_ROW = `group ${TABLE_GRID} border-b border-border/50 hover:bg-muted/50 items-center cursor-pointer`

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`

  return d.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function getFileIcon(fileType: string | null) {
  const t = fileType?.toLowerCase()
  if (t === 'pdf') return <FileText className="h-4 w-4 text-red-500 shrink-0" />
  if (t === 'xlsx' || t === 'xls' || t === 'csv')
    return <FileSpreadsheet className="h-4 w-4 text-emerald-600 shrink-0" />
  if (t === 'jpg' || t === 'jpeg' || t === 'png' || t === 'webp')
    return <FileImage className="h-4 w-4 text-blue-500 shrink-0" />
  if (t === 'docx' || t === 'doc' || t === 'txt')
    return <FileText className="h-4 w-4 text-blue-500 shrink-0" />
  if (isAudioFileType(t)) return <FileAudio className="h-4 w-4 text-amber-500 shrink-0" />
  return <File className="h-4 w-4 text-muted-foreground shrink-0" />
}

interface BreadcrumbSegment {
  name: string
  fullPath: string
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function DocumentsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // ── Import-to-case mode ─────────────────────────────────────────────────
  // When the user arrives here from a case's "Import from Data Lake" entry
  // point, `destination_case_id` is set and the page switches to a focused
  // mode: banner up top, "Import to case" bulk action, and the per-doc detail
  // sheet exposes a single-doc Import action.
  const destinationCaseId = searchParams.get('destination_case_id')
  const [destinationCase, setDestinationCase] = useState<Case | null>(null)
  const [importingBulk, setImportingBulk] = useState(false)

  useEffect(() => {
    if (!destinationCaseId) {
      setDestinationCase(null)
      return
    }
    casesApi
      .get(destinationCaseId)
      .then(setDestinationCase)
      .catch(() => {
        toast.error('Destination case not found — exited import mode')
        setSearchParams(
          prev => {
            const next = new URLSearchParams(prev)
            next.delete('destination_case_id')
            return next
          },
          { replace: true }
        )
      })
  }, [destinationCaseId, setSearchParams])

  const exitImportMode = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.delete('destination_case_id')
        return next
      },
      { replace: true }
    )
  }, [setSearchParams])

  // ── Import-mode onboarding banner ───────────────────────────────────────
  // Shows once when the user lands here from a case workspace, then either
  // auto-dismisses after 5s or is dismissed via the close button. We reset
  // visibility every time a new destinationCaseId is seen so the banner
  // re-shows for each fresh import flow (e.g. user navigated away and back).
  const [importBannerVisible, setImportBannerVisible] = useState(false)
  useEffect(() => {
    if (!destinationCaseId) {
      setImportBannerVisible(false)
      return
    }
    setImportBannerVisible(true)
    const timer = window.setTimeout(() => setImportBannerVisible(false), 5000)
    return () => window.clearTimeout(timer)
  }, [destinationCaseId])

  // ── Labels ──────────────────────────────────────────────────────────────
  const [labels, setLabels] = useState<DataLabel[]>([])
  const [labelsLoading, setLabelsLoading] = useState(true)

  // ── Current navigation state ────────────────────────────────────────────
  const [activeLabel, setActiveLabel] = useState<DataLabel | null>(null)
  const [folderStack, setFolderStack] = useState<BreadcrumbSegment[]>([])
  const currentPath = folderStack[folderStack.length - 1]?.fullPath ?? null

  // ── Browse data ─────────────────────────────────────────────────────────
  const [folders, setFolders] = useState<FolderNode[]>([])
  const [files, setFiles] = useState<FileNode[]>([])
  const [statusCountsServer, setStatusCountsServer] = useState<StatusCounts | null>(null)
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState<LakeStats | null>(null)

  // ── Retrieval search ────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('')

  // ── Status filter ───────────────────────────────────────────────────────
  const [statusFilters, setStatusFilters] = useState<Set<string>>(new Set())

  // ── Sort ─────────────────────────────────────────────────────────────────
  const [sortField, setSortField] = useState<SortField>('modified')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  // ── Selection ───────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // ── Upload sheet ────────────────────────────────────────────────────────
  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploadDestination, setUploadDestination] = useState<{
    labelId: string
    labelName: string
    labelColor: string | null
    folderPath: string | null
  } | null>(null)
  const [folderPickerOpen, setFolderPickerOpen] = useState(false)

  // ── Document detail sheet ───────────────────────────────────────────────
  const docParam = searchParams.get('doc')
  const [detailDocId, setDetailDocId] = useState<string | null>(docParam)
  const [detailOpen, setDetailOpen] = useState(!!docParam)

  // React to external `?doc=` updates (e.g. the lake AI assistant clicking a
  // document reference in a tool-call card). Without this effect the URL
  // would change but the sheet state would stay where it was at mount.
  useEffect(() => {
    if (docParam && docParam !== detailDocId) {
      setDetailDocId(docParam)
      setDetailOpen(true)
    } else if (!docParam && detailOpen) {
      setDetailOpen(false)
    }
  }, [docParam, detailDocId, detailOpen])

  const openDocument = (docId: string) => {
    setDetailDocId(docId)
    setDetailOpen(true)
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.set('doc', docId)
        next.delete('tab')
        return next
      },
      { replace: true }
    )
  }

  const closeDetail = () => {
    setDetailOpen(false)
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.delete('doc')
        next.delete('tab')
        return next
      },
      { replace: true }
    )
  }

  // ── Data loading ────────────────────────────────────────────────────────

  const loadFolderContents = useCallback(
    async (
      label: DataLabel | null,
      folderPath: string | null,
      status: DocumentStatusFilter | undefined,
      options?: { silent?: boolean }
    ) => {
      if (!label) {
        setFolders([])
        setFiles([])
        setStatusCountsServer(null)
        return
      }
      if (!options?.silent) {
        setLoading(true)
      }
      try {
        const resp = await lakeApi.getLabelFolderTree(label.id, folderPath ?? undefined, status)
        setFolders(resp.folders)
        setFiles(resp.files)
        setStatusCountsServer(resp.status_counts ?? null)
      } catch {
        // silent
      } finally {
        if (!options?.silent) {
          setLoading(false)
        }
      }
    },
    []
  )

  // The chips enforce single-select; expose the active value (or undefined).
  const activeStatusFilter: DocumentStatusFilter | undefined = useMemo(() => {
    const first = statusFilters.values().next().value
    return (first ?? undefined) as DocumentStatusFilter | undefined
  }, [statusFilters])

  // Load on nav or filter change
  useEffect(() => {
    loadFolderContents(activeLabel, currentPath, activeStatusFilter)
  }, [activeLabel, currentPath, activeStatusFilter, loadFolderContents])

  const refreshStats = useCallback(() => {
    lakeApi
      .getStats()
      .then(setStats)
      .catch(() => {})
  }, [])

  // Mirror case-documents polling: refresh the current lake view while any
  // document in the active scope is still processing.
  const hasProcessingDocuments = useMemo(() => {
    if (!activeLabel) return false
    if ((statusCountsServer?.processing ?? 0) > 0) return true
    return files.some(file => file.document.status === 'processing')
  }, [activeLabel, files, statusCountsServer])

  useEffect(() => {
    if (!hasProcessingDocuments) return

    const id = window.setInterval(() => {
      void loadFolderContents(activeLabel, currentPath, activeStatusFilter, {
        silent: true,
      })
      refreshStats()
    }, 5000)

    return () => window.clearInterval(id)
  }, [
    activeLabel,
    activeStatusFilter,
    currentPath,
    hasProcessingDocuments,
    loadFolderContents,
    refreshStats,
  ])

  // Bootstrap: load labels + stats + restore URL state
  useEffect(() => {
    lakeApi.listLabels().then(data => {
      setLabels(data)
      setLabelsLoading(false)

      const labelParam = searchParams.get('label')
      const pathParam = searchParams.get('path')
      if (labelParam) {
        const label = data.find(l => l.id === labelParam) ?? null
        if (label) {
          setActiveLabel(label)
          if (pathParam) {
            const segs = pathParam.split('/').filter(Boolean)
            setFolderStack(
              segs.map((seg, i) => ({
                name: seg,
                fullPath: segs.slice(0, i + 1).join('/'),
              }))
            )
          }
        }
      }
    })

    lakeApi
      .getStats()
      .then(setStats)
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get])

  // ── Navigation handlers ─────────────────────────────────────────────────

  const navigateToLabel = (label: DataLabel) => {
    setActiveLabel(label)
    setFolderStack([])
    setStatusFilters(new Set())
    setSelectedIds(new Set())
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.set('label', label.id)
        next.delete('path')
        return next
      },
      { replace: true }
    )
  }

  const navigateToFolder = (folder: FolderNode) => {
    setFolderStack(prev => [...prev, { name: folder.name, fullPath: folder.path }])
    setSelectedIds(new Set())
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        if (activeLabel) next.set('label', activeLabel.id)
        next.set('path', folder.path)
        return next
      },
      { replace: true }
    )
  }

  const navigateBreadcrumb = (index: number) => {
    setSelectedIds(new Set())
    if (index === -1) {
      setActiveLabel(null)
      setFolderStack([])
      setStatusFilters(new Set())
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          next.delete('label')
          next.delete('path')
          return next
        },
        { replace: true }
      )
    } else if (index === -2) {
      setFolderStack([])
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          if (activeLabel) next.set('label', activeLabel.id)
          next.delete('path')
          return next
        },
        { replace: true }
      )
    } else {
      const newStack = folderStack.slice(0, index + 1)
      setFolderStack(newStack)
      const newPath = newStack[newStack.length - 1]?.fullPath ?? null
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          if (activeLabel) next.set('label', activeLabel.id)
          if (newPath) next.set('path', newPath)
          else next.delete('path')
          return next
        },
        { replace: true }
      )
    }
  }

  // ── Sort handler ────────────────────────────────────────────────────────

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir(field === 'name' ? 'asc' : 'desc')
    }
  }

  const sortIndicator = (field: SortField) => {
    if (sortField !== field) return null
    return sortDir === 'asc' ? ' ↑' : ' ↓'
  }

  // ── Filtered & sorted data ─────────────────────────────────────────────

  const filterLower = ''

  const filteredLabels = labels.filter(l => l.name.toLowerCase().includes(filterLower))
  const filteredFolders = folders.filter(f => f.name.toLowerCase().includes(filterLower))

  const statusCounts = useMemo<Record<string, number>>(() => {
    // Inside a label/folder: use the recursive `status_counts` from the
    // folder-tree response.
    if (statusCountsServer) return statusCountsServer
    // Home view (no label selected): fall back to the global LakeStats
    // rollup so the chips reflect the full lake.
    if (stats) {
      return {
        all: stats.total_documents,
        pending: stats.pending_indexing,
        processing: stats.processing,
        indexed: stats.typesense_indexed,
        failed: stats.failed,
        skipped: 0,
      }
    }
    const zero: Record<string, number> = { all: 0 }
    for (const opt of STATUS_OPTIONS) zero[opt.value] = 0
    return zero
  }, [statusCountsServer, stats])

  const filteredFiles = useMemo(() => {
    // Status filtering happens server-side via the `status` query param;
    // here we only apply the client-side name search and the sort.
    const result = files.filter(f => f.name.toLowerCase().includes(filterLower))

    result.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1
      switch (sortField) {
        case 'name':
          return dir * a.name.localeCompare(b.name)
        case 'type': {
          const ta = a.document.file_type ?? ''
          const tb = b.document.file_type ?? ''
          return dir * ta.localeCompare(tb)
        }
        case 'size':
          return dir * (a.document.file_size - b.document.file_size)
        case 'status':
          return dir * a.document.status.localeCompare(b.document.status)
        default: {
          const da = a.document.created_at ? new Date(a.document.created_at).getTime() : 0
          const db = b.document.created_at ? new Date(b.document.created_at).getTime() : 0
          return dir * (da - db)
        }
      }
    })

    return result
  }, [files, filterLower, sortField, sortDir])

  // ── Selection helpers ───────────────────────────────────────────────────

  const allSelectableIds = useMemo(() => {
    if (!activeLabel) {
      return filteredLabels.map(l => `label:${l.id}`)
    }
    return [
      ...filteredFolders.map(f => `folder:${f.full_path}`),
      ...filteredFiles.map(f => f.document.id),
    ]
  }, [activeLabel, filteredLabels, filteredFolders, filteredFiles])

  const allSelected =
    allSelectableIds.length > 0 && allSelectableIds.every(id => selectedIds.has(id))
  const someSelected = allSelectableIds.some(id => selectedIds.has(id))

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(allSelectableIds))
    }
  }

  const lastSelectedRef = useRef<string | null>(null)

  const toggleSelect = (id: string, opts?: { shift?: boolean }) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      // Shift-click: select the contiguous range between lastSelected and id
      // (inclusive). Range is taken on `allSelectableIds` (the visible row
      // order), so it spans label/folder/file rows uniformly.
      if (opts?.shift && lastSelectedRef.current && lastSelectedRef.current !== id) {
        const a = allSelectableIds.indexOf(lastSelectedRef.current)
        const b = allSelectableIds.indexOf(id)
        if (a !== -1 && b !== -1) {
          const [lo, hi] = a < b ? [a, b] : [b, a]
          for (let i = lo; i <= hi; i++) next.add(allSelectableIds[i])
          lastSelectedRef.current = id
          return next
        }
      }
      if (next.has(id)) next.delete(id)
      else next.add(id)
      lastSelectedRef.current = id
      return next
    })
  }

  const [deleting, setDeleting] = useState(false)
  const [reprocessing, setReprocessing] = useState(false)

  const selectedDocumentIds = useMemo(
    () =>
      Array.from(selectedIds).filter(id => !id.startsWith('label:') && !id.startsWith('folder:')),
    [selectedIds]
  )

  const handleDelete = async (docId: string, name: string) => {
    if (deleting) return
    setDeleting(true)
    try {
      await lakeApi.deleteDocument(docId)
      setFiles(prev => prev.filter(f => f.document.id !== docId))
      toast.success(`Deleted "${name}"`)
      refreshStats()
    } catch {
      toast.error(`Failed to delete "${name}"`)
    } finally {
      setDeleting(false)
    }
  }

  const handleBulkDelete = async () => {
    if (deleting || selectedDocumentIds.length === 0) return
    setDeleting(true)
    try {
      const result = await lakeApi.bulkDeleteDocuments(selectedDocumentIds)
      if (result.deleted > 0) {
        toast.success(`Deleted ${result.deleted} document${result.deleted > 1 ? 's' : ''}`)
      }
      if (result.failed > 0) {
        toast.error(`Failed to delete ${result.failed} document${result.failed > 1 ? 's' : ''}`)
      }
      setSelectedIds(new Set())
      loadFolderContents(activeLabel, currentPath, activeStatusFilter)
    } catch {
      toast.error('Failed to delete documents')
    } finally {
      setDeleting(false)
    }
  }

  const handleBulkImportToCase = async () => {
    if (!destinationCaseId || importingBulk || selectedDocumentIds.length === 0) return
    setImportingBulk(true)
    try {
      const results = await Promise.allSettled(
        selectedDocumentIds.map(docId =>
          caseDocumentImportApi.upsert(docId, {
            case_id: destinationCaseId,
            scope_type: 'all',
          })
        )
      )
      const succeeded = results.filter(r => r.status === 'fulfilled').length
      const failed = results.length - succeeded
      const caseName = destinationCase?.name ?? 'case'

      if (succeeded > 0 && failed === 0) {
        toast.success(`Imported ${succeeded} document${succeeded > 1 ? 's' : ''} to ${caseName}`)
      } else if (succeeded > 0) {
        toast.warning(
          `Imported ${succeeded} of ${results.length} to ${caseName} (${failed} failed)`
        )
      } else {
        toast.error(`Failed to import to ${caseName}`)
      }

      if (succeeded > 0) {
        navigate(`/cases/${destinationCaseId}/documents`)
      }
    } finally {
      setImportingBulk(false)
    }
  }

  const handleBulkReprocess = async () => {
    if (reprocessing || selectedDocumentIds.length === 0) return

    setReprocessing(true)
    try {
      let succeeded = 0
      let failed = 0

      for (const documentId of selectedDocumentIds) {
        try {
          await lakeApi.reprocessDocument(documentId, [])
          succeeded += 1
        } catch {
          failed += 1
        }
      }

      if (succeeded > 0) {
        toast.success(`Re-ran indexing for ${succeeded} document${succeeded > 1 ? 's' : ''}`)
      }
      if (failed > 0) {
        toast.error(`Failed to re-run indexing for ${failed} document${failed > 1 ? 's' : ''}`)
      }

      if (succeeded > 0) {
        setSelectedIds(new Set())
        refreshStats()
        loadFolderContents(activeLabel, currentPath, activeStatusFilter)
      }
    } catch {
      toast.error('Failed to re-run indexing pipeline')
    } finally {
      setReprocessing(false)
    }
  }

  // ── Upload handlers ─────────────────────────────────────────────────────

  const handleOpenUpload = () => {
    if (activeLabel) {
      setUploadDestination({
        labelId: activeLabel.id,
        labelName: activeLabel.name,
        labelColor: activeLabel.color,
        folderPath: currentPath,
      })
    }
    setUploadOpen(true)
  }

  const handleFolderPickerSelect = (selection: FolderSelection) => {
    setUploadDestination({
      labelId: selection.labelId,
      labelName: selection.labelName,
      labelColor: selection.labelColor,
      folderPath: selection.path,
    })
  }

  // ── Total item count for the current view ───────────────────────────────

  const totalItems = activeLabel
    ? filteredFolders.length + filteredFiles.length
    : filteredLabels.length

  const breadcrumbItems: BreadcrumbSegment[] = activeLabel
    ? [{ name: activeLabel.name, fullPath: '' }, ...folderStack]
    : []

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <PageShell
      layout="full"
      icon={FileText}
      title={t('data.documents.title')}
      summary={t('data.documents.summary')}
      guide={{
        to: '/docs/document-processing',
        label: t('data.documents.guideLabel'),
        hint: t('data.documents.guideHint'),
      }}
      toolbar={
        <>
          {destinationCaseId && (
            <div
              className="group relative mr-1 inline-flex h-9 items-center gap-2 overflow-hidden rounded-md border border-primary/55 bg-gradient-to-r from-primary/15 via-primary/10 to-primary/5 px-3 text-xs font-medium text-primary shadow-sm shadow-primary/15"
              role="status"
              aria-live="polite"
            >
              {/* Pulsing dot — signals an active mode */}
              <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
                <span className="absolute inset-0 animate-ping rounded-full bg-primary/60 opacity-75" />
                <span className="relative h-2 w-2 rounded-full bg-primary" />
              </span>

              <Link2 className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden="true" />

              <span className="hidden sm:inline text-muted-foreground">
                {t('data.documents.importMode.addingContextTo')}
              </span>
              <span className="max-w-[180px] truncate font-semibold">
                {destinationCase?.name ?? t('data.documents.importMode.caseFallback')}
              </span>

              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-primary/70 hover:bg-primary/15 hover:text-primary max-md:-mx-1.5 max-md:h-8 max-md:w-8"
                      aria-label={t('data.documents.importMode.whatDoesThisMean')}
                    >
                      <Info className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    align="end"
                    className="max-w-[260px] text-xs leading-relaxed"
                  >
                    {t('data.documents.importMode.tooltip', {
                      name:
                        destinationCase?.name ?? t('data.documents.importMode.linkedCaseFallback'),
                    })}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <button
                type="button"
                onClick={exitImportMode}
                aria-label="Exit import mode"
                className="-mr-1 inline-flex h-5 w-5 items-center justify-center rounded-sm text-primary/70 hover:bg-primary/15 hover:text-primary max-md:-mr-2.5 max-md:-ml-1.5 max-md:h-8 max-md:w-8"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
          {/* Not available yet (always disabled) — the phone toolbar keeps the room. */}
          <Button variant="outline" size="xs" disabled className="max-md:hidden">
            <FolderPlus />
            {t('data.documents.newFolder')}
          </Button>
          <Button size="xs" className="max-md:h-9" onClick={handleOpenUpload}>
            <Upload />
            {t('data.documents.uploadFiles')}
          </Button>
        </>
      }
    >
      {/* Everything above the stats bar is one column. On the phone it scrolls as a
          whole (the table takes its natural height), so short screens, the import
          banner or a selection never squeeze the table down to a row or two. */}
      <div className="flex min-h-0 flex-1 flex-col max-md:overflow-y-auto">
        {/* Import-mode onboarding banner — inline, between toolbar and search.
          Auto-dismisses after 5s; click X to close manually. */}
        {destinationCaseId && importBannerVisible && (
          <div className="shrink-0 px-4 pt-3">
            <div className="group relative overflow-hidden rounded-lg border border-primary/35 bg-gradient-to-r from-primary/14 via-primary/8 to-transparent p-4 shadow-sm shadow-primary/10">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-primary/30 bg-primary/15 text-primary">
                  <Link2 className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">
                    Adding context to{' '}
                    <span className="text-primary">{destinationCase?.name ?? 'this case'}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
                    Open any lake document and use <strong>Import to case</strong> to copy it into
                    this case — page-scope your import as needed. Click <strong>×</strong> on the
                    toolbar pill to exit import mode.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setImportBannerVisible(false)}
                  aria-label="Dismiss banner"
                  className="ml-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-primary/10 hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              {/* Auto-dismiss progress bar — depletes left→right over 5s */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute bottom-0 left-0 h-0.5 w-full origin-left bg-primary/40"
                style={{ animation: 'import-banner-decay 5s linear forwards' }}
              />
              <style>{`
              @keyframes import-banner-decay {
                from { transform: scaleX(1); }
                to { transform: scaleX(0); }
              }
            `}</style>
            </div>
          </div>
        )}

        {/* Toolbar: search + status filter chips */}
        <div className="shrink-0 px-4 pt-3 pb-3">
          <LakeSearchCombobox
            value={searchQuery}
            onChange={setSearchQuery}
            onSelectDocument={openDocument}
            onSelectChunk={chunk => openDocument(chunk.document_id)}
          />

          {activeLabel && (
            <div className="scrollbar-none mt-3 flex items-center gap-2 flex-wrap max-md:-mx-4 max-md:flex-nowrap max-md:overflow-x-auto max-md:px-4 max-md:[&>*]:shrink-0">
              <FilterChip
                label="All"
                count={statusCounts.all}
                active={statusFilters.size === 0}
                onClick={() => setStatusFilters(new Set())}
              />
              {STATUS_OPTIONS.map(opt => (
                <FilterChip
                  key={opt.value}
                  label={opt.label}
                  count={statusCounts[opt.value] ?? 0}
                  active={statusFilters.has(opt.value)}
                  onClick={() => {
                    setStatusFilters(prev => {
                      const next = new Set<string>()
                      if (!prev.has(opt.value)) next.add(opt.value)
                      return next
                    })
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Document area — phone: as tall as its rows (at least 24rem), never shrunk */}
        <div className="mt-2 flex-1 flex flex-col overflow-hidden px-4 pb-4 min-h-0 max-md:min-h-[24rem] max-md:shrink-0 max-md:basis-auto max-md:overflow-visible">
          {/* Breadcrumb — phone: the crumbs carry the row's vertical padding (28 px tap targets) */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground px-2 py-0.5 shrink-0 md:py-2">
            <FolderClosed className="h-3.5 w-3.5 shrink-0" />
            <div className="scrollbar-none flex items-center gap-1 max-md:min-w-0 max-md:overflow-x-auto max-md:whitespace-nowrap">
              {!activeLabel ? (
                <span className="text-foreground font-medium max-md:py-1.5">Home</span>
              ) : (
                <>
                  <button
                    className="hover:text-foreground transition-colors cursor-pointer max-md:py-1.5"
                    onClick={() => navigateBreadcrumb(-1)}
                  >
                    Home
                  </button>
                  {breadcrumbItems.map((seg, i) => {
                    const isLast = i === breadcrumbItems.length - 1
                    const isLabelRoot = i === 0
                    return (
                      <span
                        key={`${seg.fullPath}-${i}`}
                        className="flex items-center gap-1 max-md:shrink-0"
                      >
                        <ChevronRight className="h-3 w-3 text-muted-foreground/50" />
                        {isLast ? (
                          <span className="text-foreground font-medium truncate max-w-[160px] max-md:py-1.5">
                            {seg.name}
                          </span>
                        ) : (
                          <button
                            className="hover:text-foreground transition-colors cursor-pointer truncate max-w-[160px] max-md:py-1.5"
                            onClick={() =>
                              isLabelRoot ? navigateBreadcrumb(-2) : navigateBreadcrumb(i - 1)
                            }
                          >
                            {seg.name}
                          </button>
                        )}
                      </span>
                    )
                  })}
                </>
              )}
            </div>
            <span className="ml-auto max-md:shrink-0">
              {loading || labelsLoading ? (
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
              ) : (
                `${totalItems} ${totalItems === 1 ? 'item' : 'items'}`
              )}
            </span>
          </div>

          {/* Table container */}
          <Card className="flex flex-1 flex-col overflow-hidden rounded-xl p-0 min-h-0">
            {/* Table header */}
            <div
              className={`${TABLE_GRID} border-b border-border bg-muted/30 text-[11px] font-medium text-muted-foreground uppercase tracking-wider shrink-0`}
              style={{
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingTop: '8px',
                paddingBottom: '8px',
              }}
            >
              <div className={HEADER_CHECKBOX_HIT_AREA} onClick={toggleSelectAll}>
                <Checkbox
                  checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                  aria-label="Select all"
                />
              </div>
              <button
                className="text-left hover:text-foreground transition-colors max-md:-my-2 max-md:py-2"
                onClick={() => handleSort('name')}
              >
                Name{sortIndicator('name')}
              </button>
              <button
                className="text-left hover:text-foreground transition-colors max-md:hidden"
                onClick={() => handleSort('type')}
              >
                Type{sortIndicator('type')}
              </button>
              <button
                className="text-left hover:text-foreground transition-colors max-md:hidden"
                onClick={() => handleSort('size')}
              >
                Size{sortIndicator('size')}
              </button>
              <button
                className="text-left hover:text-foreground transition-colors max-md:hidden"
                onClick={() => handleSort('status')}
              >
                Status{sortIndicator('status')}
              </button>
              <button
                className="text-left hover:text-foreground transition-colors max-md:hidden"
                onClick={() => handleSort('modified')}
              >
                Modified{sortIndicator('modified')}
              </button>
              <div />
            </div>

            {/* Table body */}
            <div className="flex-1 overflow-auto">
              {(loading && activeLabel) || labelsLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : !activeLabel ? (
                filteredLabels.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <FolderClosed className="h-8 w-8 text-muted-foreground mb-2" />
                    <h3 className="text-sm font-semibold text-foreground mb-1">No labels</h3>
                    <p className="text-xs text-muted-foreground max-w-sm">
                      {'Labels organize documents in your data lake.'}
                    </p>
                  </div>
                ) : (
                  filteredLabels.map(label => {
                    const labelKey = `label:${label.id}`
                    const isSelected = selectedIds.has(labelKey)
                    const docCount =
                      label.document_count != null
                        ? `${label.document_count} ${label.document_count === 1 ? 'doc' : 'docs'}`
                        : ''
                    return (
                      <div
                        key={label.id}
                        className={cn(TABLE_ROW, isSelected && 'bg-primary/5')}
                        style={{
                          paddingLeft: '16px',
                          paddingRight: '16px',
                          paddingTop: '10px',
                          paddingBottom: '10px',
                        }}
                        onClick={() => navigateToLabel(label)}
                      >
                        <div
                          className={ROW_CHECKBOX_HIT_AREA}
                          onClick={e => {
                            e.stopPropagation()
                            toggleSelect(labelKey, { shift: e.shiftKey })
                          }}
                        >
                          <Checkbox checked={isSelected} aria-label={`Select ${label.name}`} />
                        </div>
                        <div className="flex items-center gap-2 text-sm text-foreground font-medium truncate">
                          <FolderClosed className="h-4 w-4 text-amber-500 shrink-0" />
                          <span className="truncate">{label.name}</span>
                          {label.color && (
                            <span
                              className="h-2 w-2 rounded-full shrink-0"
                              style={{ backgroundColor: label.color }}
                            />
                          )}
                          {/* The phone hides the count column, so the count rides next to the name. */}
                          {docCount && (
                            <span className="text-xs text-muted-foreground font-normal shrink-0 md:hidden">
                              {docCount}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground max-md:hidden">Folder</div>
                        <div className="text-xs text-muted-foreground max-md:hidden">
                          {docCount}
                        </div>
                        <div className="max-md:hidden" />
                        <div className="max-md:hidden" />
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity max-md:opacity-100">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8 md:size-6"
                                onClick={e => e.stopPropagation()}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() =>
                                  toast.info('Label deletion is not supported from here')
                                }
                              >
                                <Trash2 className="h-4 w-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    )
                  })
                )
              ) : filteredFolders.length === 0 && filteredFiles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <FileText className="h-8 w-8 text-muted-foreground mb-2" />
                  <h3 className="text-sm font-semibold text-foreground mb-1">No documents</h3>
                  <p className="text-xs text-muted-foreground max-w-sm">
                    {statusFilters.size > 0
                      ? 'No documents match your filters.'
                      : 'Upload files to this label to see them here.'}
                  </p>
                </div>
              ) : (
                <>
                  {filteredFolders.map(folder => {
                    const folderKey = `folder:${folder.full_path}`
                    const isSelected = selectedIds.has(folderKey)
                    const filteredCount = activeStatusFilter
                      ? (folder.file_count_by_status?.[activeStatusFilter] ?? 0)
                      : folder.file_count
                    const dim = activeStatusFilter !== undefined && filteredCount === 0
                    return (
                      <div
                        key={folder.full_path}
                        className={cn(TABLE_ROW, isSelected && 'bg-primary/5', dim && 'opacity-50')}
                        style={{
                          paddingLeft: '16px',
                          paddingRight: '16px',
                          paddingTop: '10px',
                          paddingBottom: '10px',
                        }}
                        onClick={() => navigateToFolder(folder)}
                      >
                        <div
                          className={ROW_CHECKBOX_HIT_AREA}
                          onClick={e => {
                            e.stopPropagation()
                            toggleSelect(folderKey, { shift: e.shiftKey })
                          }}
                        >
                          <Checkbox checked={isSelected} aria-label={`Select ${folder.name}`} />
                        </div>
                        <div className="flex items-center gap-2 text-sm text-foreground font-medium truncate">
                          <FolderClosed className="h-4 w-4 text-amber-500 shrink-0" />
                          <span className="truncate">{folder.name}</span>
                          <span className="text-xs text-muted-foreground font-normal shrink-0">
                            {activeStatusFilter
                              ? `${filteredCount} ${activeStatusFilter}`
                              : `${folder.file_count} ${folder.file_count === 1 ? 'file' : 'files'}`}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground max-md:hidden">Folder</div>
                        <div className="text-xs text-muted-foreground max-md:hidden" />
                        <div className="max-md:hidden" />
                        <div className="max-md:hidden" />
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity max-md:opacity-100">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8 md:size-6"
                                onClick={e => e.stopPropagation()}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => toast.info('Folder deletion is not supported yet')}
                              >
                                <Trash2 className="h-4 w-4 mr-2" /> Delete Folder
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    )
                  })}

                  {filteredFiles.map(fileNode => {
                    const doc = fileNode.document
                    const isSelected = selectedIds.has(doc.id)
                    return (
                      <div
                        key={doc.id}
                        className={cn(TABLE_ROW, isSelected && 'bg-primary/5')}
                        style={{
                          paddingLeft: '16px',
                          paddingRight: '16px',
                          paddingTop: '10px',
                          paddingBottom: '10px',
                        }}
                        onClick={() => openDocument(doc.id)}
                      >
                        <div
                          className={ROW_CHECKBOX_HIT_AREA}
                          onClick={e => {
                            e.stopPropagation()
                            toggleSelect(doc.id, { shift: e.shiftKey })
                          }}
                        >
                          <Checkbox checked={isSelected} aria-label={`Select ${fileNode.name}`} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 text-sm text-foreground truncate">
                            {getFileIcon(doc.file_type)}
                            <span className="truncate">{fileNode.name}</span>
                          </div>
                          <DocumentRowPhoneMeta
                            status={doc.indexing_status ?? doc.status}
                            fileType={doc.file_type}
                            size={formatFileSize(doc.file_size)}
                            date={doc.created_at ? formatDate(doc.created_at) : null}
                          />
                        </div>
                        <div className="text-xs text-muted-foreground uppercase max-md:hidden">
                          {doc.file_type || '-'}
                        </div>
                        <div className="text-xs text-muted-foreground max-md:hidden">
                          {formatFileSize(doc.file_size)}
                        </div>
                        <div className="text-xs max-md:hidden">
                          <StatusBadge status={doc.indexing_status ?? doc.status} />
                        </div>
                        <div className="text-xs text-muted-foreground max-md:hidden">
                          {doc.created_at ? formatDate(doc.created_at) : '-'}
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity max-md:opacity-100">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8 md:size-6"
                                onClick={e => e.stopPropagation()}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleDelete(doc.id, fileNode.name)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    )
                  })}
                </>
              )}
            </div>
          </Card>

          {/* Selection bar — phone: stays in view while the list scrolls under it */}
          {selectedIds.size > 0 && (
            <div className="shrink-0 pt-3 max-md:sticky max-md:bottom-2 max-md:z-10">
              <Card className="flex h-10 flex-row items-center gap-2 rounded-lg px-3 p-0 max-md:h-12 max-md:gap-1 max-md:px-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-9 md:size-7"
                  onClick={() => setSelectedIds(new Set())}
                >
                  <X className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground max-md:min-w-0 max-md:truncate">
                  {selectedIds.size} selected
                </span>
                <div className="ml-auto flex items-center gap-1 max-md:shrink-0">
                  {destinationCaseId && (
                    <Button
                      variant="default"
                      size="sm"
                      disabled={
                        importingBulk ||
                        reprocessing ||
                        deleting ||
                        selectedDocumentIds.length === 0
                      }
                      onClick={handleBulkImportToCase}
                    >
                      {importingBulk ? (
                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      ) : (
                        <ArrowRight className="h-4 w-4 mr-1.5 max-md:hidden" />
                      )}
                      Import to case
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="max-md:size-9 max-md:px-0"
                    disabled={reprocessing || deleting || selectedDocumentIds.length === 0}
                    onClick={handleBulkReprocess}
                  >
                    {reprocessing ? (
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin max-md:mr-0" />
                    ) : (
                      <RotateCcw className="h-4 w-4 mr-1.5 max-md:mr-0" />
                    )}
                    <span className="max-md:sr-only">Re-run indexing</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive max-md:size-9 max-md:px-0"
                    disabled={deleting || reprocessing || selectedDocumentIds.length === 0}
                    onClick={handleBulkDelete}
                  >
                    {deleting ? (
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin max-md:mr-0" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-1.5 max-md:mr-0" />
                    )}
                    <span className="max-md:sr-only">Delete</span>
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Expandable stats sheet */}
      <DocumentsStatsSheet stats={stats} />

      {/* Upload Sheet */}
      <Sheet open={uploadOpen} onOpenChange={setUploadOpen}>
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Upload Files</SheetTitle>
          </SheetHeader>
          <div className="mt-4 flex-1 min-h-0 overflow-y-auto">
            <LakeUpload
              destination={uploadDestination}
              onSelectDestination={() => setFolderPickerOpen(true)}
              onUploadComplete={() => {
                loadFolderContents(activeLabel, currentPath, activeStatusFilter)
                lakeApi
                  .getStats()
                  .then(setStats)
                  .catch(() => {})
              }}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Folder picker for upload destination */}
      <LakeFolderPickerSheet
        open={folderPickerOpen}
        onClose={() => setFolderPickerOpen(false)}
        labels={labels}
        onSelect={handleFolderPickerSelect}
        title="Select upload destination"
        confirmLabel="Select"
      />

      {/* Document detail sheet */}
      <LakeDetailSheet
        docId={detailDocId}
        open={detailOpen}
        onClose={closeDetail}
        labels={labels}
        onReprocessed={() => {
          // Silent refresh: the reprocess kicks off the page's processing-poll
          // (also silent), so the list updates its values in place without the
          // loading spinner blanking and re-rendering the whole list (flicker).
          loadFolderContents(activeLabel, currentPath, activeStatusFilter, { silent: true })
        }}
        destinationCaseId={destinationCaseId}
        destinationCaseName={destinationCase?.name ?? null}
        onImportSucceeded={() => {
          if (destinationCaseId) {
            navigate(`/cases/${destinationCaseId}/documents`)
          }
        }}
      />
    </PageShell>
  )
}
